import {
  Box,
  Button,
  Collapse,
  IconButton,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import { MdCancel } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { itemsPerPage } from "../../constant";
import { getErrorMessage, getSuccessMessage } from "../../functions";
import { removeCustomer, updateCustomer } from "../../redux/customerSlice";
import { addCustomer, deleteCustomer } from "../../services/customerApi";
import ConfirmDialog from "../../UI/ConfirmDialog";
import DataTable from "../../UI/DataTable";
import InputField from "../../UI/InputField";
import ListPagination from "../../UI/ListPagination";
import PageHeader from "../../UI/PageHeader";

const Customers = () => {
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [customerDetails, setCustomerDetails] = useState({
    name: "",
    address: "",
    phoneNumber: "",
    gst: "",
    email: "",
    whatsappNumber: "",
  });
  const dispatch = useDispatch();
  const customers = useSelector((state) => state.customer.customers);
  const isMobileView = useSelector((state) => state.mobileView.isMobileView);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredProducts = customers.filter(
    (product) =>
      (product.name &&
        product.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (product.address &&
        product.address.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (product.phoneNumber &&
        product.phoneNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (product.email &&
        product.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (product.gst &&
        product.gst.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (product.whatsappNumber &&
        product.whatsappNumber.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const pageCount = Math.ceil(filteredProducts.length / itemsPerPage);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCustomerDetails({
      ...customerDetails,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await addCustomer({
        ...customerDetails,
        isEdit: !!editingId,
      });
      dispatch(updateCustomer(res.data));
      setCustomerDetails({
        name: "",
        address: "",
        phoneNumber: "",
        gst: "",
        email: "",
        whatsappNumber: "",
      });
      setEditingId(null);
      setShowForm(false);
      toast.success(getSuccessMessage(res));
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (customer) => {
    setCustomerDetails(customer);
    setEditingId(customer._id);
    setShowForm(true);
  };

  const confirmDelete = async () => {
    if (!customerToDelete) return;
    setDeleting(true);
    try {
      await deleteCustomer(customerToDelete._id);
      dispatch(removeCustomer(customerToDelete._id));
      toast.success("Customer removed");
      setCustomerToDelete(null);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  const handlePageClick = (data) => {
    const selectedPage = data.selected;
    setCurrentPage(selectedPage);
  };
  const offset = currentPage * itemsPerPage;
  const endOffset = offset + itemsPerPage;

  const currentProducts = filteredProducts.slice(
    offset,
    Math.min(endOffset, filteredProducts.length)
  );

  const cancelForm = () => {
    setEditingId(null);
    setShowForm(false);
    setCustomerDetails({
      name: "",
      address: "",
      phoneNumber: "",
      gst: "",
      email: "",
      whatsappNumber: "",
    });
  };

  const isEmpty = customers.length === 0;
  const noResults = !isEmpty && filteredProducts.length === 0;

  return (
    <div>
      <PageHeader
        title="Customers"
        description="Manage billing contacts, GST details, and communication preferences."
        actions={
          <Button
            colorScheme="brand"
            size="md"
            onClick={() => {
              if (showForm) {
                cancelForm();
              } else {
                setEditingId(null);
                setCustomerDetails({
                  name: "",
                  address: "",
                  phoneNumber: "",
                  gst: "",
                  email: "",
                  whatsappNumber: "",
                });
                setShowForm(true);
              }
            }}
          >
            {showForm ? "Close form" : "Add customer"}
          </Button>
        }
      />

      <Collapse in={showForm} animateOpacity>
        <Box
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="xl"
          bg="white"
          p={{ base: 4, md: 6 }}
          mb={8}
          shadow="sm"
        >
          <h2 className="mb-4 text-base font-semibold text-gray-800">
            {editingId ? "Edit customer" : "New customer"}
          </h2>
          <form
            className="flex flex-col gap-4 lg:flex-row lg:flex-wrap"
            onSubmit={handleSubmit}
          >
            <InputField
              labelName="Name"
              type="text"
              required
              uni="name"
              name="name"
              value={customerDetails.name}
              onChange={handleChange}
            />
            <InputField
              labelName="Address"
              type="text"
              required
              uni="address"
              name="address"
              value={customerDetails.address}
              onChange={handleChange}
            />
            <InputField
              labelName="Phone number"
              type="tel"
              required
              uni="phoneNumber"
              name="phoneNumber"
              value={customerDetails.phoneNumber}
              onChange={handleChange}
            />
            <InputField
              labelName="GST"
              type="text"
              uni="gst"
              name="gst"
              value={customerDetails.gst}
              onChange={handleChange}
            />
            <InputField
              labelName="Email"
              type="email"
              uni="email"
              name="email"
              value={customerDetails.email}
              onChange={handleChange}
            />
            <InputField
              labelName="WhatsApp number"
              type="tel"
              uni="whatsappNumber"
              name="whatsappNumber"
              value={customerDetails.whatsappNumber}
              onChange={handleChange}
            />
            <div className="flex w-full flex-wrap items-end gap-3 pt-2">
              <Button
                type="submit"
                colorScheme="brand"
                size="md"
                isLoading={submitting}
              >
                {editingId ? "Save changes" : "Save customer"}
              </Button>
              <Button variant="ghost" size="md" type="button" onClick={cancelForm}>
                Cancel
              </Button>
            </div>
          </form>
        </Box>
      </Collapse>

      <div className="mb-6 max-w-md">
        <label
          htmlFor="customer-search"
          className="mb-1.5 block text-sm font-medium text-secondary"
        >
          Search
        </label>
        <input
          id="customer-search"
          type="search"
          placeholder="Search by name, phone, email, GST…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="min-h-10 w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-secondary shadow-sm transition-colors duration-200 placeholder:text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
      </div>

      {isEmpty ? (
        <Box
          borderWidth="1px"
          borderStyle="dashed"
          borderColor="gray.300"
          borderRadius="xl"
          py={16}
          px={6}
          textAlign="center"
          bg="white"
        >
          <p className="text-base font-medium text-gray-800">
            No customers yet
          </p>
          <p className="mt-2 text-sm text-gray-600">
            Add your first customer to start creating invoices with correct
            billing details.
          </p>
          <Button
            colorScheme="brand"
            mt={6}
            onClick={() => {
              setEditingId(null);
              setShowForm(true);
            }}
          >
            Add customer
          </Button>
        </Box>
      ) : noResults ? (
        <Box py={12} textAlign="center" color="gray.600" fontSize="sm">
          No customers match your search. Try a different keyword.
        </Box>
      ) : (
        <div className="overflow-y-auto">
          {isMobileView ? (
            <div className="flex flex-col gap-4">
              {currentProducts.map((customer, index) => (
                <div
                  key={customer._id || index}
                  className="rounded-xl border border-border bg-surface p-4 shadow-sm transition-shadow duration-200 hover:shadow-md"
                >
                  <div className="font-semibold text-secondary">
                    {customer.name}
                  </div>
                  <div className="mt-2 flex flex-col gap-1 text-sm text-muted">
                    <div>
                      <span className="font-medium text-secondary/80">
                        Address:{" "}
                      </span>
                      {customer.address}
                    </div>
                    <div>
                      <span className="font-medium text-secondary/80">
                        Phone:{" "}
                      </span>
                      {customer.phoneNumber}
                    </div>
                    <div>
                      <span className="font-medium text-secondary/80">
                        GST:{" "}
                      </span>{" "}
                      {customer.gst}
                    </div>
                    <div>
                      <span className="font-medium text-secondary/80">
                        Email:{" "}
                      </span>
                      {customer.email}
                    </div>
                    <div>
                      <span className="font-medium text-secondary/80">
                        WhatsApp:{" "}
                      </span>
                      {customer.whatsappNumber}
                    </div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    {editingId === customer._id ? (
                      <IconButton
                        aria-label="Cancel editing"
                        variant="ghost"
                        icon={<MdCancel size={20} />}
                        onClick={cancelForm}
                      />
                    ) : (
                      <>
                        <IconButton
                          aria-label={`Edit ${customer.name}`}
                          variant="ghost"
                          colorScheme="brand"
                          icon={<FaEdit size={18} />}
                          onClick={() => handleEdit(customer)}
                        />
                        <IconButton
                          aria-label={`Delete ${customer.name}`}
                          variant="ghost"
                          colorScheme="red"
                          icon={<FaTrashAlt size={18} />}
                          onClick={() => setCustomerToDelete(customer)}
                        />
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <DataTable className="mt-2" variant="striped">
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Address</Th>
                  <Th>Phone</Th>
                  <Th>GST</Th>
                  <Th>Email</Th>
                  <Th>WhatsApp</Th>
                  <Th textAlign="right">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {currentProducts.map((customer, index) => (
                  <Tr
                    key={customer._id || index}
                    _hover={{ bg: "gray.50" }}
                    transition="background 0.15s ease"
                  >
                    <Td>{customer.name}</Td>
                    <Td>{customer.address}</Td>
                    <Td>{customer.phoneNumber}</Td>
                    <Td>{customer.gst}</Td>
                    <Td>{customer.email}</Td>
                    <Td>{customer.whatsappNumber}</Td>
                    <Td textAlign="right">
                      <div className="flex justify-end gap-1">
                        {editingId === customer._id ? (
                          <IconButton
                            aria-label="Cancel editing"
                            size="sm"
                            variant="ghost"
                            icon={<MdCancel size={18} />}
                            onClick={cancelForm}
                          />
                        ) : (
                          <>
                            <IconButton
                              aria-label={`Edit ${customer.name}`}
                              size="sm"
                              variant="ghost"
                              colorScheme="brand"
                              icon={<FaEdit size={16} />}
                              onClick={() => handleEdit(customer)}
                            />
                            <IconButton
                              aria-label={`Delete ${customer.name}`}
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              icon={<FaTrashAlt size={16} />}
                              onClick={() => setCustomerToDelete(customer)}
                            />
                          </>
                        )}
                      </div>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </DataTable>
          )}
        </div>
      )}

      {!isEmpty && !noResults ? (
        <ListPagination
          pageCount={pageCount}
          onPageChange={handlePageClick}
          isMobileView={isMobileView}
        />
      ) : null}

      <ConfirmDialog
        isOpen={!!customerToDelete}
        onClose={() => setCustomerToDelete(null)}
        onConfirm={confirmDelete}
        title="Delete this customer?"
        body={`${
          customerToDelete?.name ?? "This customer"
        } will be removed from your list. This does not delete past invoices.`}
        confirmText="Delete customer"
        cancelText="Cancel"
        isLoading={deleting}
      />
    </div>
  );
};

export default Customers;
